import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { slides } from '../../../data/home/home';

const AUTO_PLAY_INTERVAL = 5000;
const TRANSITION_DURATION = 1000; // Increased for smoother transitions
const TEXT_ANIMATION_DELAY = 400; // Delay between each text element

const HomeSlider: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [textAnim, setTextAnim] = useState({
    h1: false,
    h2: false,
    p: false,
    button: false
  });
  const [currentText, setCurrentText] = useState(slides[0]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const nextSlide = () => {
    setPrev(current);
    setCurrent((prev) => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    setPrev(current);
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  };
  const goToSlide = (idx: number) => {
    setPrev(current);
    setCurrent(idx);
  };

  // Auto play
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(nextSlide, AUTO_PLAY_INTERVAL);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [current]);

  // Enhanced transition effect
  useEffect(() => {
    // Reset all text animations
    setTextAnim({
      h1: false,
      h2: false,
      p: false,
      button: false
    });
    
    setIsTransitioning(true);
    
    // After image transition starts, begin text animations
    const startTextAnimation = setTimeout(() => {
      setIsTransitioning(false);
      setCurrentText(slides[current]);
      
      // Animate each text element with a delay
      setTimeout(() => setTextAnim(prev => ({ ...prev, h1: true })), 0);
      setTimeout(() => setTextAnim(prev => ({ ...prev, h2: true })), TEXT_ANIMATION_DELAY);
      setTimeout(() => setTextAnim(prev => ({ ...prev, p: true })), TEXT_ANIMATION_DELAY * 2);
      setTimeout(() => setTextAnim(prev => ({ ...prev, button: true })), TEXT_ANIMATION_DELAY * 3);
    }, TRANSITION_DURATION / 2);

    return () => clearTimeout(startTextAnimation);
  }, [current]);

  return (
    <div className="relative w-full h-[420px] md:h-[520px] lg:h-[600px] flex items-center justify-center overflow-hidden bg-black shadow-lg">
      {/* Previous image for blur out */}
      {isTransitioning && (
        <img
          src={slides[prev].image}
          alt="prev"
          className="absolute inset-0 w-full h-full object-cover object-center z-10 transition-all duration-1000 ease-in-out blur-md opacity-80"
          style={{ pointerEvents: 'none' }}
        />
      )}
      {/* Current image */}
      <img
        src={slides[current].image}
        alt={slides[current].h1}
        className={`absolute inset-0 w-full h-full object-cover object-center z-0 transition-all duration-1000 ease-in-out ${isTransitioning ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}
        style={{ pointerEvents: 'none' }}
        draggable={false}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 z-20" />
      {/* Text Content with enhanced animation */}
      <div className="absolute left-0 top-0 h-full flex flex-col justify-center pl-6 pr-6 md:pl-16 md:pr-0 max-w-[90%] md:max-w-[50%] text-left text-white font-primary z-30">
        <h1 className={`text-3xl md:text-5xl font-bold mb-2 leading-tight drop-shadow-lg transition-all duration-700 ease-out ${textAnim.h1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {currentText.h1}
        </h1>
        <h2 className={`text-xl md:text-2xl font-semibold mb-3 drop-shadow-lg transition-all duration-700 ease-out ${textAnim.h2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {currentText.h2}
        </h2>
        <p className={`text-base md:text-lg mb-6 drop-shadow-lg transition-all duration-700 ease-out ${textAnim.p ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {currentText.p}
        </p>
        <button
          className={`w-fit px-8 py-3 rounded-lg bg-green-300 hover:bg-green-400 text-black font-medium text-lg transition-all duration-700 ease-out shadow-md ${textAnim.button ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          onClick={() => navigate('/about')}
        >
          See More
        </button>
      </div>
      {/* Navigation Buttons */}
      <button
        className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-black rounded-full w-10 h-10 flex items-center justify-center shadow-lg z-40 transition-colors duration-200 opacity-20 hover:opacity-100"
        onClick={prevSlide}
        aria-label="Previous Slide"
      >
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/70 hover:bg-white text-black rounded-full w-10 h-10 flex items-center justify-center shadow-lg z-40 transition-colors duration-200 opacity-20 hover:opacity-100"
        onClick={nextSlide}
        aria-label="Next Slide"
      >
        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-40">
        {slides.map((_, idx) => (
          <button
            key={idx}
            className={`w-3 h-3 rounded-full border-2 border-white transition-all duration-200 ${idx === current ? 'bg-green-300 border-green-400 scale-125' : 'bg-white/40'}`}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
      {/* Mobile adjustments and animation styles */}
      <style>{`
        @media (max-width: 640px) {
          .font-primary { font-size: 0.95rem; }
          .text-3xl { font-size: 1.6rem; }
          .text-xl { font-size: 1.1rem; }
          .text-lg { font-size: 1rem; }
          .h-[420px] { height: 260px !important; }
        }
        .translate-y-8 { transform: translateY(2rem); }
        .translate-y-0 { transform: translateY(0); }
        .blur-md { filter: blur(16px); }
      `}</style>
    </div>
  );
};

export default HomeSlider; 